import { IndexedDb } from "utils/IndexedDb";

// Map to track File objects and their IndexedDB IDs
export const fileIdMap = new Map<File, string>();

// Helper type for file reference (stored in localStorage)
interface FileReference {
  __type: "FileRef";
  __id: string;
}

// Helper to check if an object is a FileReference
export function isFileReference(obj: unknown): obj is FileReference {
  return (
    obj !== null &&
    typeof obj === "object" &&
    "__type" in obj &&
    obj.__type === "FileRef" &&
    "__id" in obj &&
    typeof obj.__id === "string"
  );
}

// Replace Files with FileReferences and store files in IndexedDB
export async function serializeFiles<T>(obj: T): Promise<T> {
  if (obj instanceof File) {
    // Check if this File already has an ID (from previous deserialization)
    let id = fileIdMap.get(obj);

    if (!id) {
      // File doesn't have an ID yet, store it in IndexedDB
      id = await IndexedDb.storeItem(obj);
      fileIdMap.set(obj, id);
    }

    return {
      __type: "FileRef",
      __id: id,
    } as T;
  }

  if (Array.isArray(obj)) {
    return (await Promise.all(obj.map((item) => serializeFiles(item)))) as T;
  }

  if (obj && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = await serializeFiles(
          (obj as Record<string, unknown>)[key]
        );
      }
    }
    return result as T;
  }

  return obj;
}

// Replace FileReferences with actual Files from IndexedDB
export async function deserializeFiles<T>(obj: T): Promise<T> {
  if (isFileReference(obj)) {
    const file = await IndexedDb.getItem(obj.__id);

    // If file is not found in IndexedDB, log warning and return null
    if (file === null) {
      console.warn(
        `File with id ${obj.__id} not found in IndexedDB. It may have been deleted.`
      );
      return null as T;
    }

    // Store the ID in the Map so we can reuse it during next serialization
    if (file instanceof File) {
      fileIdMap.set(file, obj.__id);
    }

    return file as T;
  }

  if (Array.isArray(obj)) {
    const deserialized = await Promise.all(
      obj.map((item) => deserializeFiles(item))
    );
    return deserialized as T;
  }

  if (obj && typeof obj === "object") {
    const result: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = await deserializeFiles(
          (obj as Record<string, unknown>)[key]
        );
      }
    }
    return result as T;
  }

  return obj;
}

// Collect all file IDs from the state
export function collectFileIds(
  obj: unknown,
  ids: Set<string> = new Set()
): Set<string> {
  if (isFileReference(obj)) {
    ids.add(obj.__id);
    return ids;
  }

  if (Array.isArray(obj)) {
    obj.forEach((item) => collectFileIds(item, ids));
    return ids;
  }

  if (obj && typeof obj === "object") {
    for (const key in obj as Record<string, unknown>) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        collectFileIds((obj as Record<string, unknown>)[key], ids);
      }
    }
  }

  return ids;
}
